import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import {
  ClipboardCheck,
  Phone,
  ArrowRight,
  CheckCircle2,
  Loader2,
  User,
  Mail,
  MapPin,
  Target,
  FileText,
  Video,
  AlertCircle,
  Lock,
} from 'lucide-react';
import { BASIC_KEYS, PROGRAM_FORMS } from '../components/apply/programForms';
import { ApplyHero, ElevateMethod, HelpCard, OrderSummary, PLAN_OPTIONS, PlanPicker, Step, WhatsAppIcon, inputCls } from '../components/apply/ApplyParts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Map URL params to plan values
const getPlanFromParam = (param) => {
  const planMap = {
    'clarity-session': 'Career Clarity Session (₹499)',
    'career-blueprint': 'Career Direction Blueprint (₹2,999)',
    'career-readiness': 'Career Readiness Program (₹5,999)',
    'role-skills': 'Role Skills Execution (₹3,499)',
    'integrated-journey': 'Integrated Career Journey (₹8,500)',
    // Legacy params
    'profile-builder': 'Career Direction Blueprint (₹2,999)',
    'interview-ready': 'Career Readiness Program (₹5,999)',
    'clarity-call': 'Career Clarity Session (₹499)',
    'launch-kit': 'Career Direction Blueprint (₹2,999)',
    'career-jumpstart': 'Career Readiness Program (₹5,999)',
    'diagnostic': 'Career Clarity Session (₹499)',
  };
  return planMap[param] || '';
};

// Map plan names to package IDs
const getPackageId = (planName) => {
  const packageMap = {
    'Career Clarity Session (₹499)': 'clarity-session',
    'Career Direction Blueprint (₹2,999)': 'career-blueprint',
    'Career Readiness Program (₹5,999)': 'career-readiness',
    'Role Skills Execution (₹3,499)': 'role-skills',
    'Integrated Career Journey (₹8,500)': 'integrated-journey',
  };
  return packageMap[planName] || null;
};

export const Apply = () => {
  const [searchParams] = useSearchParams();
  const planParam = searchParams.get('plan') || '';

  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    city: '',
    current_status: '',
    degree_stream: '',
    college_name: '',
    career_clarity: '',
    target_role: '',
    biggest_challenge: '',
    why_join: '',
    interested_plan: getPlanFromParam(planParam),
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [error, setError] = useState('');
  const [roles, setRoles] = useState([]);

  useEffect(() => {
    fetch(`${API_URL}/api/journey/role-options`).then((r) => r.json()).then(setRoles).catch(() => setRoles([]));
  }, []);

  // Prefill contact details when arriving from the Readiness Check results
  useEffect(() => {
    const raw = sessionStorage.getItem('elevate_booking_prefill');
    if (!raw) return;
    sessionStorage.removeItem('elevate_booking_prefill');
    try {
      const p = JSON.parse(raw);
      setFormData((prev) => ({ ...prev, full_name: prev.full_name || p.full_name || '', email: prev.email || p.email || '', phone: prev.phone || p.phone || '' }));
    } catch (e) { /* ignore malformed prefill */ }
  }, []);

  // Update plan if URL parameter changes
  useEffect(() => {
    if (planParam) {
      setFormData((prev) => ({ ...prev, interested_plan: getPlanFromParam(planParam) }));
    }
  }, [planParam]);

  // Load Razorpay script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const getPlanPrice = (plan) => {
    const prices = {
      'Career Clarity Session (₹499)': '₹499',
      'Career Direction Blueprint (₹2,999)': '₹2,999',
      'Career Readiness Program (₹5,999)': '₹5,999',
      'Role Skills Execution (₹3,499)': '₹3,499',
      'Integrated Career Journey (₹8,500)': '₹8,500',
    };
    return prices[plan] || '';
  };

  const getPlanName = (plan) => {
    const names = {
      'Career Clarity Session (₹499)': 'Career Clarity Session',
      'Career Direction Blueprint (₹2,999)': 'Career Direction Blueprint',
      'Career Readiness Program (₹5,999)': 'Career Readiness Program',
      'Role Skills Execution (₹3,499)': 'Role Skills Execution',
      'Integrated Career Journey (₹8,500)': 'Integrated Career Journey',
    };
    return names[plan] || plan;
  };

  const getPlanDuration = (plan) => {
    const durations = {
      'Career Clarity Session (₹499)': '45 mins',
      'Career Direction Blueprint (₹2,999)': '~2 weeks',
      'Career Readiness Program (₹5,999)': '8 weeks',
      'Role Skills Execution (₹3,499)': '8 weeks',
      'Integrated Career Journey (₹8,500)': '8 weeks',
    };
    return durations[plan] || '';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  // Validate form before payment
  const planSteps = PROGRAM_FORMS[getPackageId(formData.interested_plan)] || [];
  const planFields = planSteps.flatMap((st) => st.fields);

  const validateForm = () => {
    if (!formData.interested_plan) return 'Please select a package to proceed with payment';
    if (!formData.full_name.trim()) return 'Please enter your full name';
    if (!formData.email.trim()) return 'Please enter your email';
    if (!formData.phone.trim()) return 'Please enter your phone number';
    const missing = planFields.find((f) => f.required && !String(formData[f.key] || '').trim());
    return missing ? `Please fill in: ${missing.label}` : null;
  };

  const buildPayload = () => {
    const keys = [...BASIC_KEYS, ...planFields.map((f) => f.key)];
    const data = Object.fromEntries(keys.filter((k) => formData[k]).map((k) => [k, formData[k]]));
    const role = roles.find((r) => r.family === formData.target_role_family);
    if (role && data.target_role_family) { data.target_role_title = role.family_title; data.target_role = role.family_title; }
    return data;
  };

  // PAYMENT-FIRST FLOW: Submit form triggers payment, application saved only after successful payment
  const adminToken = sessionStorage.getItem('admin_token');
  const simulatePayment = async () => {
    const err = validateForm();
    if (err) { setError(err); return; }
    setError(''); setIsProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/payments/simulate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({ package_id: getPackageId(formData.interested_plan), application_data: buildPayload() }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.detail || 'Simulation failed. Sign in to admin again.');
      setPaymentSuccess(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) { setError(e.message); } finally { setIsProcessing(false); }
  };

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    const packageId = getPackageId(formData.interested_plan);
    if (!packageId) {
      setError('Please select a valid package');
      return;
    }

    setIsProcessing(true);
    setError('');

    try {
      // Step 1: Create Razorpay order (application NOT saved yet)
      const orderResponse = await fetch(`${API_URL}/api/payments/create-order-direct`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          package_id: packageId,
          customer_name: formData.full_name,
          customer_email: formData.email,
          customer_phone: formData.phone,
        }),
      });

      if (!orderResponse.ok) {
        const errorData = await orderResponse.json();
        throw new Error(errorData.detail || 'Failed to create order');
      }

      const orderData = await orderResponse.json();

      // Step 2: Open Razorpay checkout
      const options = {
        key: orderData.key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'Elevate Careers',
        description: orderData.package_name,
        order_id: orderData.order_id,
        prefill: orderData.prefill,
        image: `${window.location.origin}/logo-elevate-mark.png?v=2`,
        theme: {
          color: '#0b1f4d',
        },
        handler: async function (response) {
          // Step 3: Payment successful - now save application with payment details
          try {
            const saveResponse = await fetch(`${API_URL}/api/payments/complete-with-application`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                application_data: buildPayload(),
              }),
            });

            if (saveResponse.ok) {
              setPaymentSuccess(true);
            } else {
              const errorData = await saveResponse.json();
              setError(errorData.detail || 'Payment received but failed to save application. Please contact support.');
            }
          } catch (err) {
            console.error('Save application error:', err);
            setError('Payment received but failed to save application. Please contact support with your payment ID: ' + response.razorpay_payment_id);
          }
          setIsProcessing(false);
        },
        modal: {
          ondismiss: function () {
            setIsProcessing(false);
          },
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', function (response) {
        setError(response.error.description || 'Payment failed. Please try again.');
        setIsProcessing(false);
      });
      razorpay.open();
    } catch (err) {
      console.error('Payment flow error:', err);
      setError(err.message || 'Failed to initiate payment');
      setIsProcessing(false);
    }
  }, [formData, roles]);

  // What you get based on selected plan
  const planBenefits = {
    'Career Clarity Session (₹499)': [
      { icon: Video, text: '45-minute 1-on-1 video call' },
      { icon: Target, text: 'Discuss your background and goals' },
      { icon: FileText, text: 'Explore roles that fit your profile' },
      { icon: ClipboardCheck, text: 'Get immediate action steps' },
    ],
    'Career Direction Blueprint (₹2,999)': [
      { icon: Target, text: 'Career assessment' },
      { icon: FileText, text: 'Role-fit recommendations' },
      { icon: ClipboardCheck, text: 'Skills-gap review' },
      { icon: Video, text: 'Personalised career roadmap' },
    ],
    'Career Readiness Program (₹5,999)': [
      { icon: FileText, text: 'Career roadmap' },
      { icon: Target, text: 'Resume & LinkedIn support' },
      { icon: Video, text: 'Interview preparation' },
      { icon: Phone, text: 'Industry mentoring' },
    ],
    'Role Skills Execution (₹3,499)': [
      { icon: Target, text: 'Practical missions for your target role' },
      { icon: ClipboardCheck, text: 'Consultant-reviewed work and feedback' },
      { icon: Video, text: '8 weekly human check-ins' },
      { icon: FileText, text: 'Portfolio mini-project' },
    ],
    'Integrated Career Journey (₹8,500)': [
      { icon: Target, text: 'Role Skills Execution missions' },
      { icon: FileText, text: 'Resume, LinkedIn & interview preparation' },
      { icon: Video, text: '8 weekly human check-ins' },
      { icon: ClipboardCheck, text: 'Specialist and milestone reviews' },
    ],
  };

  const isSessionPlan = !formData.interested_plan || formData.interested_plan.startsWith('Career Clarity');

  // Payment Success Screen
  if (paymentSuccess) {
    const next = [
      ['Confirmation email', 'A receipt and your next steps are on their way to your inbox.'],
      ['WhatsApp connect', `Our team will reach out within 24 hours to schedule your ${isSessionPlan ? 'session' : 'kickoff call'}.`],
      ['Open your dashboard', 'Sign in at My Career Journey with this email to track everything.'],
    ];
    return (
      <div data-testid="apply-page">
        <section className="min-h-[80vh] bg-[#0b1f4d] text-white flex items-center py-16">
          <div className="max-w-2xl mx-auto px-5 text-center" data-testid="payment-success">
            <div className="w-20 h-20 rounded-full bg-teal-400 flex items-center justify-center mx-auto mb-6"><CheckCircle2 size={40} className="text-[#0b1f4d]" /></div>
            <h1 className="font-outfit font-bold text-4xl sm:text-5xl">Payment successful</h1>
            <p className="text-lg text-slate-300 mt-4">Thank you, {formData.full_name.split(' ')[0]}! Your payment for <span className="font-semibold text-teal-300">{getPlanName(formData.interested_plan)}</span> is confirmed.</p>
            <ol className="mt-10 space-y-3 text-left">
              {next.map(([t, d], i) => (
                <li key={t} className="flex gap-4 rounded-2xl bg-white/[0.05] border border-white/10 p-5">
                  <span className="w-8 h-8 rounded-full bg-teal-400 text-[#0b1f4d] font-bold text-sm flex items-center justify-center shrink-0">{i + 1}</span>
                  <div><p className="font-semibold">{t}</p><p className="text-sm text-slate-400 mt-0.5">{d}</p></div>
                </li>
              ))}
            </ol>
            <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/my-journey" data-testid="success-dashboard-btn" className="inline-flex items-center justify-center gap-2 bg-teal-400 hover:bg-teal-300 text-[#0b1f4d] font-semibold px-7 py-3.5 rounded-full transition-colors">Go to My Career Journey <ArrowRight size={16} /></Link>
              <a href="https://wa.me/919311510040" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 border border-white/20 hover:bg-white/10 font-semibold px-7 py-3.5 rounded-full transition-colors"><WhatsAppIcon className="w-4 h-4" /> Chat on WhatsApp</a>
            </div>
          </div>
        </section>
      </div>
    );
  }

  const selectedPlanBenefits = planBenefits[formData.interested_plan] || [];
  const isSession = isSessionPlan;
  const hasValidPlan = formData.interested_plan && formData.interested_plan !== 'Not sure yet';
  const field = (id, label, Icon, props = {}) => (
    <div className={props.wide ? 'sm:col-span-2' : ''}>
      <Label htmlFor={id} className="text-slate-700 font-medium flex items-center gap-2 mb-2 text-sm"><Icon size={15} className="text-teal-600" />{label}</Label>
      <Input id={id} name={id} value={formData[id] || ''} onChange={handleChange} placeholder={props.placeholder} type={props.type || 'text'}
        className={inputCls} data-testid={props.testid} />
    </div>
  );
  const renderField = (f) => {
    const label = `${f.label}${f.required ? ' *' : ''}`;
    if (f.type === 'text') return <div key={f.key} className={f.wide ? 'sm:col-span-2' : ''}>{field(f.key, label, f.icon, f)}</div>;
    if (f.type === 'textarea') return (
      <div key={f.key} className="sm:col-span-2">
        <Label htmlFor={f.key} className="text-slate-700 font-medium flex items-center gap-2 mb-2 text-sm"><f.icon size={15} className="text-teal-600" />{label}</Label>
        <Textarea id={f.key} name={f.key} value={formData[f.key] || ''} onChange={handleChange} rows={f.rows || 3}
          placeholder={f.placeholder} className={`${inputCls} h-auto resize-none`} data-testid={f.testid} />
      </div>
    );
    const opts = f.type === 'role' ? roles.map((r) => [r.family, r.family_title]) : f.options.map((o) => [o, o]);
    return (
      <div key={f.key} className={f.wide ? 'sm:col-span-2' : ''}>
        <Label className="text-slate-700 font-medium flex items-center gap-2 mb-2 text-sm"><f.icon size={15} className="text-teal-600" />{label}</Label>
        <Select value={formData[f.key] || ''} onValueChange={(v) => handleSelectChange(f.key, v)}>
          <SelectTrigger className={inputCls} data-testid={f.testid}><SelectValue placeholder={f.type === 'role' ? 'Choose the role you are aiming for' : 'Select an option'} /></SelectTrigger>
          <SelectContent>{opts.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>
      </div>
    );
  };

  return (
    <div data-testid="apply-page" className="bg-slate-50">
      <ApplyHero isSession={isSession} />
      <section className="py-10 md:py-14">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid lg:grid-cols-5 gap-8 lg:gap-10">
            <div className="lg:col-span-3">
              <form onSubmit={handleSubmit} noValidate className="space-y-5">
                <Step n={1} title={isSession ? 'Your session' : 'Your program'} hint="The questions below adjust to what you choose." testid="apply-step-plan">
                  <PlanPicker value={formData.interested_plan} onChange={(v) => handleSelectChange('interested_plan', v)} />
                </Step>

                <Step n={2} title="Basic information" hint={`So we can confirm your ${isSession ? 'booking' : 'enrollment'} and reach you.`} testid="apply-step-basic">
                  <div className="grid sm:grid-cols-2 gap-4">
                    {field('full_name', 'Full name *', User, { wide: true, placeholder: 'Enter your full name', testid: 'full-name-input' })}
                    {field('email', 'Email *', Mail, { type: 'email', placeholder: 'your@email.com', testid: 'email-input' })}
                    {field('phone', 'Phone (WhatsApp) *', Phone, { type: 'tel', placeholder: '+91 98765 43210', testid: 'phone-input' })}
                    {field('city', 'City', MapPin, { wide: true, placeholder: 'Your city', testid: 'city-input' })}
                  </div>
                </Step>

                {planSteps.map((st, i) => (
                  <Step key={st.testid} n={i + 3} title={st.title} hint={st.hint} testid={st.testid}>
                    <div className="grid sm:grid-cols-2 gap-4">{st.fields.map(renderField)}</div>
                  </Step>
                ))}

                <div className="rounded-2xl border border-slate-200 bg-white p-6 md:p-7 space-y-5">
                  {hasValidPlan && (
                    <div className="lg:hidden flex items-center justify-between rounded-xl bg-[#0b1f4d] text-white px-4 py-3" data-testid="apply-mobile-summary">
                      <span className="text-sm font-semibold">{getPlanName(formData.interested_plan)}</span>
                      <span className="font-outfit font-bold text-teal-300">{getPlanPrice(formData.interested_plan)}</span>
                    </div>
                  )}
                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3" data-testid="apply-error">
                      <AlertCircle size={20} className="text-red-500 flex-shrink-0" /><p className="text-red-600 text-sm">{error}</p>
                    </div>
                  )}
                  <button type="submit" disabled={isProcessing || !hasValidPlan} data-testid="submit-application-btn"
                    className="group w-full inline-flex items-center justify-center gap-2 bg-teal-400 hover:bg-teal-300 text-[#0b1f4d] rounded-full py-4 text-base font-bold transition-[background-color,transform] hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0">
                    {isProcessing ? (<><Loader2 size={20} className="animate-spin" /> Processing...</>)
                      : hasValidPlan ? (<><Lock size={18} /> Pay {getPlanPrice(formData.interested_plan)} & Confirm {isSession ? 'Booking' : 'Enrollment'}<ArrowRight size={18} className="transition-transform group-hover:translate-x-1" /></>)
                      : 'Select a package to continue'}
                  </button>
                  <p className="text-center text-xs text-slate-500">Your application is saved only after successful payment. Payments are processed securely by Razorpay.</p>
                  {adminToken && hasValidPlan && (
                    <div className="rounded-xl border-2 border-dashed border-amber-300 bg-amber-50 p-4" data-testid="simulate-payment-box">
                      <p className="text-xs font-semibold text-amber-800 uppercase tracking-wider">Admin test mode</p>
                      <p className="text-xs text-amber-700 mt-1">Runs the full paid flow (application, emails, dashboard unlock) without charging. Only visible because you are signed in to admin.</p>
                      <button type="button" onClick={simulatePayment} disabled={isProcessing} data-testid="simulate-payment-btn"
                        className="mt-3 w-full rounded-full border border-amber-400 bg-white hover:bg-amber-100 text-amber-900 font-semibold py-2.5 text-sm transition-colors disabled:opacity-50">
                        Simulate payment (test)
                      </button>
                    </div>
                  )}
                </div>
              </form>
            </div>

            <aside className="lg:col-span-2">
              <div className="lg:sticky lg:top-24 space-y-5">
                {hasValidPlan && <OrderSummary name={getPlanName(formData.interested_plan)} price={getPlanPrice(formData.interested_plan)} was={PLAN_OPTIONS.find((o) => o.value === formData.interested_plan)?.was} duration={getPlanDuration(formData.interested_plan)} benefits={selectedPlanBenefits} />}
                <ElevateMethod />
                <HelpCard />
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Apply;
